import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useFirmaAccountId, useFirmaContext } from '@/contexts/FirmaContext'
import Spinner from '@/components/Spinner'
import ErrorAlert from '@/components/ErrorAlert'
import EmptyState from '@/components/EmptyState'
import Avatar from '@/components/Avatar'
import { useRightPanel } from '@/contexts/RightPanelContext'
import {
  ChevronRight, ChevronDown, Download, Info,
  GraduationCap, Calendar, Clock, FileText, Printer, X,
} from 'lucide-react'
import type {
  TnPersonKomplett, TnPhoto, TnStudiengang, Note,
} from '@/types'

/* ── Helpers ─────────────────────────────────────────────────── */

function parseDate(raw: string | null | undefined): Date | null {
  if (!raw) return null
  const m = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)
  if (m) return new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]))
  const d = new Date(raw)
  return isNaN(d.getTime()) ? null : d
}

function fmtDate(raw: string | null | undefined): string {
  if (!raw) return '–'
  const d = parseDate(raw)
  if (!d) return raw
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/* ── Status dot colors ───────────────────────────────────────── */

type DotStatus = 'bestanden' | 'angemeldet' | 'nicht_bestanden'

function getDotStatus(notePruefung: number): DotStatus {
  if (notePruefung > 0 && notePruefung <= 4.0) return 'bestanden'
  if (notePruefung > 4.0) return 'nicht_bestanden'
  return 'angemeldet'
}

function StatusDot({ status }: { status: DotStatus }) {
  const colors: Record<DotStatus, string> = {
    bestanden: 'bg-emerald-500',
    angemeldet: 'bg-amber-400',
    nicht_bestanden: 'bg-red-500',
  }
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${colors[status]} flex-shrink-0`} />
}

function fmtNote(n: number): string {
  if (n > 0) return n.toFixed(1).replace('.', ',')
  return '-'
}

function fmtKurzDatum(raw: string | null | undefined): string {
  if (!raw) return '–'
  const d = parseDate(raw)
  if (!d) return raw
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/* ── Data grouping: Noten → Semester → Module → Teilleistungen ── */

interface Teilleistung {
  id: number
  name: string
  note: number
  status: DotStatus
  anmeldeDatum: string | null
  klausurDatum: string | null
  klausurUhrzeit: string | null
  klausurort: string | null
}

interface Modul {
  fachId: number
  fach: string
  ects: number
  bestanden: boolean
  teilleistungen: Teilleistung[]
}

interface SemesterGruppe {
  name: string
  module: Modul[]
  totalEcts: number
  erreichtEcts: number
}

function groupNotenBySemester(noten: Note[], regelSemester: number): SemesterGruppe[] {
  // Schritt 1: Alle Noten nach FachID zu Modulen gruppieren
  const allModules = new Map<number, Modul>()
  const fachgebietMap = new Map<number, string>()

  for (const n of noten) {
    fachgebietMap.set(n.FachID, n.Fachgebiet || 'Sonstiges')
    if (!allModules.has(n.FachID)) {
      allModules.set(n.FachID, {
        fachId: n.FachID,
        fach: n.Fach,
        ects: n.EinzelCredits,
        bestanden: n.BestandenFach || (n.NoteFach > 0 && n.NoteFach <= 4.0),
        teilleistungen: [],
      })
    }
    allModules.get(n.FachID)!.teilleistungen.push({
      id: n.TeilleistungsID,
      name: n.Prüfung || n.Teilleistung || '–',
      note: n.NotePrüfung,
      status: getDotStatus(n.NotePrüfung),
      anmeldeDatum: n.AnmeldeDatum || null,
      klausurDatum: n.KlausurDatum || null,
      klausurUhrzeit: n.KlausurUhrzeit || null,
      klausurort: n.Klausurort || null,
    })
  }

  // Schritt 2: Module in "reguläre Module" und "Sonder-Fachgebiete" trennen
  const regularModules: Modul[] = []
  const sonderGruppen = new Map<string, Modul[]>()

  for (const [fachId, modul] of allModules) {
    const fg = fachgebietMap.get(fachId) || 'Sonstiges'
    if (fg === 'Module') {
      regularModules.push(modul)
    } else {
      if (!sonderGruppen.has(fg)) sonderGruppen.set(fg, [])
      sonderGruppen.get(fg)!.push(modul)
    }
  }

  // Schritt 3: Reguläre Module nach FachID sortieren und in Semester aufteilen
  regularModules.sort((a, b) => a.fachId - b.fachId)

  const result: SemesterGruppe[] = []
  const hasSonder = sonderGruppen.size > 0
  const moduleSemesters = hasSonder ? Math.max(1, regelSemester - 1) : regelSemester
  const perSemester = Math.ceil(regularModules.length / moduleSemesters)

  for (let i = 0; i < moduleSemesters; i++) {
    const start = i * perSemester
    const end = Math.min(start + perSemester, regularModules.length)
    const semModule = regularModules.slice(start, end)
    if (semModule.length === 0) continue
    const totalEcts = semModule.reduce((sum, m) => sum + m.ects, 0)
    const erreichtEcts = semModule.filter(m => m.bestanden).reduce((sum, m) => sum + m.ects, 0)
    result.push({
      name: `${i + 1}. Semester`,
      module: semModule,
      totalEcts,
      erreichtEcts,
    })
  }

  // Schritt 4: Sonder-Fachgebiete (Thesis, Verteidigung etc.) als letztes Semester
  if (hasSonder) {
    const lastSemModule: Modul[] = []
    let lastEcts = 0
    for (const [, mods] of sonderGruppen) {
      for (const m of mods) {
        lastSemModule.push(m)
        lastEcts += m.ects
      }
    }
    const lastErreicht = lastSemModule.filter(m => m.bestanden).reduce((sum, m) => sum + m.ects, 0)
    result.push({
      name: `${regelSemester}. Semester`,
      module: lastSemModule,
      totalEcts: lastEcts,
      erreichtEcts: lastErreicht,
    })
  }

  return result
}

/* ── Studienfortschritt berechnen ──────────────────────────────── */

interface StudienFortschritt {
  istCredits: number
  sollCredits: number
  regelCredits: number
}

/** Schätzt Fachsemester aus imma_seit + heute (als Fallback wenn TN-API null liefert).
 *  6 Monate pro Semester; Start zählt als 1. Semester. */
function semesterFromImmaSeit(immaSeit: string | null | undefined): number | null {
  const start = parseDate(immaSeit)
  if (!start) return null
  const now = new Date()
  const months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth())
  return Math.max(1, Math.floor(months / 6) + 1)
}

function berechneFortschritt(
  sg: TnStudiengang | undefined,
  semesterGruppen: SemesterGruppe[],
): StudienFortschritt | null {
  if (!sg) return null

  const sgName = (sg.studiengang || '').toLowerCase()
  const isMaster = sgName.includes('master')
  const regelSemester = isMaster ? 4 : 7
  const regelCredits = isMaster ? 120 : 180
  const creditsProSemester = regelCredits / regelSemester

  // istCredits aus den Semester-Gruppen aggregieren — garantiert identisch zur Anzeige
  // unten (gleiche Dedupe-Logik per FachID, gleicher bestanden-Filter inkl. anerkannter Fächer).
  const istCredits = semesterGruppen.reduce((sum, g) => sum + g.erreichtEcts, 0)

  // Fachsemester aus TN-API; Fallback auf Berechnung aus imma_seit, wenn das Feld null oder 1
  // ist und der Studi nachweislich schon weiter ist (imma_seit liegt > 6 Monate zurück).
  const semesterFromApi = sg.fachsemester ?? null
  const semesterFromDate = semesterFromImmaSeit(sg.imma_seit || sg.anmeldung)
  const semester = semesterFromApi && semesterFromApi > 1
    ? semesterFromApi
    : (semesterFromDate ?? semesterFromApi ?? 1)
  const sollCredits = Math.max(0, Math.min(regelSemester, semester) - 1) * creditsProSemester

  return { istCredits, sollCredits, regelCredits }
}

/* ── TeilnehmerAvatar ─────────────────────────────────────────── */

function TeilnehmerAvatar({ accountId, personenId, photos, name, size = 'lg' }: {
  accountId: string
  personenId: number
  photos?: TnPhoto[]
  name: string
  size?: 'lg' | 'sm'
}) {
  const hasPhoto = photos && photos.length > 0 && photos.some(p => p.has_picture || p.has_picture_hq)
  const pxSize = size === 'lg' ? 112 : 56
  return (
    <Avatar
      photoUrl={hasPhoto ? api.teilnehmerPhotoUrl(accountId, personenId) : null}
      fallbackName={name}
      size={pxSize}
    />
  )
}

/* ── Akkordeon-Item (wiederverwendbar) ────────────────────────── */

function AccordionItem({ title, defaultOpen = false, badge, children }: {
  title: string
  defaultOpen?: boolean
  badge?: React.ReactNode
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 px-1 text-left hover:bg-gray-50/50 transition-colors cursor-pointer"
      >
        <span className="font-semibold text-gray-900 text-[0.95rem]">{title}</span>
        <div className="flex items-center gap-3">
          {badge}
          {open
            ? <ChevronDown size={18} className="text-gray-400" />
            : <ChevronRight size={18} className="text-gray-400" />
          }
        </div>
      </button>
      {open && (
        <div className="pb-5 px-1">
          {children}
        </div>
      )}
    </div>
  )
}

/* ── Einfacher Expandable-Link (für Semesterpläne / PO) ──────── */


/* ══════════════════════════════════════════════════════════════ */
/* ═══  MAIN PAGE  ════════════════════════════════════════════= */
/* ══════════════════════════════════════════════════════════════ */

export default function TeilnehmerDetailPage() {
  const { firmaToken, personenId, stnId } = useParams<{ firmaToken: string; personenId: string; stnId?: string }>()
  const accountId = useFirmaAccountId()
  const { canRead } = useFirmaContext()
  const canReadNoten = canRead('noten')

  const { data: person, isLoading, error } = useQuery({
    queryKey: ['teilnehmerDetail', accountId, personenId],
    queryFn: () => api.teilnehmerDetail(accountId!, Number(personenId)),
    enabled: !!accountId && !!personenId,
  })

  // Firma laden, um die STNs zu erhalten, die TATSÄCHLICH zu dieser Firma gehören.
  // Person kann mehrere Studiengänge über mehrere Firmen haben — der Backend-/Berechtigungs-
  // Check (personen_firmen_zuordnung) lässt nur die firmenzugeordneten STNs zu.
  const { data: firmaData } = useQuery({
    queryKey: ['firma', accountId],
    queryFn: () => api.firma(accountId!),
    enabled: !!accountId,
  })

  const firmaStnIdsForPerson = firmaData?.teilnehmer
    ?.filter(t => t.PersonenFID === Number(personenId))
    .map(t => t.StudiengangTnId) ?? []

  // STN-Auflösung:
  // 1) Wenn URL-Parameter vorhanden, diesen verwenden (vom User explizit gewählt)
  // 2) Sonst: erste aktive Studiengang-ID aus TN-API, die zu dieser Firma gehört
  // 3) Fallback: irgendeine Firma-zugeordnete STN
  const resolvedStnId = stnId
    ? Number(stnId)
    : person?.studiengaenge?.find(
        (s) => s.ist_aktiv && s.studiengang_tn_id != null && firmaStnIdsForPerson.includes(s.studiengang_tn_id)
      )?.studiengang_tn_id
      ?? person?.studiengaenge?.find(
        (s) => s.studiengang_tn_id != null && firmaStnIdsForPerson.includes(s.studiengang_tn_id)
      )?.studiengang_tn_id
      ?? firmaStnIdsForPerson[0]
      ?? null

  const { data: notenData } = useQuery({
    queryKey: ['noten', accountId, resolvedStnId],
    queryFn: () => api.noten(accountId!, resolvedStnId!),
    enabled: !!accountId && !!resolvedStnId && canReadNoten,
  })

  // Rechtes Panel: Firma beibehalten
  const { setFirma, clearFirma } = useRightPanel()
  useEffect(() => {
    if (accountId) setFirma(accountId, '')
    return () => clearFirma()
  }, [accountId, setFirma, clearFirma])

  if (isLoading) {
    return <div className="flex justify-center py-20"><Spinner /></div>
  }
  if (error) {
    return (
      <div className="px-6 py-8">
        <ErrorAlert message={(error as Error).message} />
      </div>
    )
  }
  if (!person) {
    return (
      <div className="px-6 py-8">
        <EmptyState message="Person nicht gefunden" icon />
      </div>
    )
  }

  // aktiverSg an resolvedStnId koppeln, damit Fachsemester / regelSemester / Noten
  // aus EINEM konsistenten Studiengang stammen. Fallback auf ersten aktiven Studi.
  const aktiveSg = person.studiengaenge?.filter((s) => s.ist_aktiv) ?? []
  const aktiverSg =
    person.studiengaenge?.find((s) => s.studiengang_tn_id === resolvedStnId)
    ?? aktiveSg[0]
    ?? person.studiengaenge?.[0]
  const noten = notenData?.noten ?? []
  const sgName = (aktiverSg?.studiengang || '').toLowerCase()
  const isMaster = sgName.includes('master')
  const regelSemester = isMaster ? 4 : 7
  const semesterGruppen = groupNotenBySemester(noten, regelSemester)
  const fortschritt = berechneFortschritt(aktiverSg, semesterGruppen)
  const fullName = `${person.vorname} ${person.nachname}`

  return (
    <div className="px-10 lg:px-16 py-6">
      {/* ── Breadcrumb ─────────────────────────────────────── */}
      <nav className="flex items-center gap-1.5 text-sm mb-8">
        <Link to="/" className="text-fom-600 hover:text-fom-700 hover:underline">Startseite</Link>
        <span className="text-gray-400">/</span>
        {accountId && (
          <>
            <Link to={`/firma/${firmaToken}#teilnehmer`} className="text-fom-600 hover:text-fom-700 hover:underline">
              Alle Studierenden
            </Link>
            <span className="text-gray-400">/</span>
          </>
        )}
        <span className="text-gray-600">{fullName}</span>
      </nav>

      {/* ── Two-Column Layout ──────────────────────────────── */}
      <div className="flex gap-8 items-start">

        {/* ────── LEFT: Profile Sidebar ────── */}
        <aside className="w-72 flex-shrink-0 sticky top-36 self-start max-h-[calc(100vh-160px)] overflow-y-auto">
          <ProfileSidebar
            accountId={accountId!}
            personenId={Number(personenId)}
            person={person}
            aktiverSg={aktiverSg}
            stnId={resolvedStnId}
            canReadNoten={canReadNoten}
          />
        </aside>

        {/* ────── RIGHT: Main Content ────── */}
        <main className="flex-1 min-w-0">

          {/* ── Section: Studienverlauf & Prüfungsleistung ── */}
          <StudienverlaufSection
            semesterGruppen={semesterGruppen}
            accountId={accountId}
            stnId={resolvedStnId}
            fortschritt={fortschritt}
            canRead={canReadNoten}
          />

          {/* ── Dashed Divider ── */}
          <div className="border-t-2 border-dashed border-fom-400 my-10" />

          {/* ── Section: Semesterpläne ── */}
          <SemesterplaeneSection accountId={accountId} stnId={resolvedStnId} />

          {/* ── Section: Prüfungsordnungen ── */}
          <PruefungsordnungenSection accountId={accountId} stnId={resolvedStnId} noten={noten} />
        </main>
      </div>
    </div>
  )
}

/* ══════════════════════════════════════════════════════════════ */
/* ═══  PROFILE SIDEBAR  ═════════════════════════════════════= */
/* ══════════════════════════════════════════════════════════════ */

function ProfileSidebar({ accountId, personenId, person, aktiverSg, stnId, canReadNoten }: {
  accountId: string
  personenId: number
  person: TnPersonKomplett
  aktiverSg?: TnStudiengang
  stnId?: number | null
  canReadNoten: boolean
}) {
  const fullName = `${person.vorname} ${person.nachname}`
  const studienort = aktiverSg?.studienort ? `Hochschulzentrum ${aktiverSg.studienort}` : ''
  const [showLeistungsnachweis, setShowLeistungsnachweis] = useState(false)

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-card">
      {/* Photo + Name */}
      <div className="flex flex-col items-center text-center mb-6">
        <TeilnehmerAvatar
          accountId={accountId}
          personenId={personenId}
          photos={person.photos}
          name={fullName}
          size="lg"
        />
        <h2 className="mt-4 text-lg font-bold text-gray-900">{fullName}</h2>
        <p className="text-sm text-gray-500">FOM Hochschule</p>
        {studienort && <p className="text-xs text-gray-400">{studienort}</p>}
      </div>

      {/* Study Info */}
      <div className="space-y-3 text-sm border-t border-gray-100 pt-5">
        {aktiverSg && (
          <>
            <InfoRow
              icon={<GraduationCap size={14} className="text-gray-400" />}
              label="Studiengang:"
              value={[aktiverSg.studiengang, aktiverSg.studienfach].filter(Boolean).join('\n') || '–'}
            />
            <InfoRow
              icon={<Clock size={14} className="text-gray-400" />}
              label="Fachsemester:"
              value={aktiverSg.fachsemester ? `${aktiverSg.fachsemester}. Semester` : '–'}
            />
            <InfoRow
              icon={<Calendar size={14} className="text-gray-400" />}
              label="Studienstart:"
              value={`Start ${fmtDate(aktiverSg.imma_seit || aktiverSg.anmeldung)}`}
            />
          </>
        )}
      </div>

      {/* Downloads — Leistungsnachweis ist Noten-basierter Inhalt; ohne
          noten:read wird der Block komplett ausgeblendet. Backend lehnt die
          PDF-Anfrage zusätzlich mit 403 ab. */}
      {canReadNoten && (
        <div className="border-t border-gray-100 mt-5 pt-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Downloads:</p>
          {stnId ? (
            <button
              type="button"
              onClick={() => setShowLeistungsnachweis(true)}
              className="w-full border border-gray-200 rounded-xl p-3 flex items-center gap-3 hover:border-fom-300 transition-colors cursor-pointer group bg-white text-left"
            >
              <div className="w-10 h-10 rounded-lg bg-fom-50 flex items-center justify-center group-hover:bg-fom-100 transition-colors">
                <FileText size={18} className="text-fom-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400">PDF</p>
                <p className="text-sm font-semibold text-fom-600">Leistungsnachweis</p>
              </div>
            </button>
          ) : (
            <div className="border border-gray-200 rounded-xl p-3 flex items-center gap-3 opacity-50">
              <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center">
                <FileText size={18} className="text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400">PDF</p>
                <p className="text-sm font-semibold text-gray-400">Leistungsnachweis</p>
              </div>
            </div>
          )}
        </div>
      )}

      {canReadNoten && showLeistungsnachweis && stnId && (
        <LeistungsnachweisModal
          accountId={accountId}
          stnId={stnId}
          onClose={() => setShowLeistungsnachweis(false)}
        />
      )}
    </div>
  )
}

function LeistungsnachweisModal({ accountId, stnId, onClose }: {
  accountId: string
  stnId: number
  onClose: () => void
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [loaded, setLoaded] = useState(false)
  const pdfUrl = api.leistungsnachweisUrl(accountId, stnId)

  const handlePrint = () => {
    iframeRef.current?.contentWindow?.focus()
    iframeRef.current?.contentWindow?.print()
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose])

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-lg shadow-2xl w-[90vw] max-w-4xl h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50">
          <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <FileText size={16} className="text-fom-500" />
            Leistungsnachweis
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handlePrint}
              disabled={!loaded}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[0.75rem] font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Printer size={13} />
              Drucken
            </button>
            <a
              href={`${pdfUrl}?download=1`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[0.75rem] font-medium bg-fom-500 text-white hover:bg-fom-600 transition-colors no-underline ${loaded ? '' : 'pointer-events-none opacity-40'}`}
            >
              <Download size={13} />
              Speichern
            </a>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-200 transition-colors border-none cursor-pointer bg-transparent"
              aria-label="Schließen"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="flex-1 bg-gray-100 relative">
          {!loaded && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gray-100 z-10">
              <Spinner />
              <p className="text-sm text-gray-500">Leistungsnachweis wird geladen…</p>
            </div>
          )}
          <iframe
            ref={iframeRef}
            src={pdfUrl}
            onLoad={() => setLoaded(true)}
            className="w-full h-full border-none"
            title="Leistungsnachweis"
          />
        </div>
      </div>
    </div>,
    document.body,
  )
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 flex-shrink-0">{icon}</span>
      <div className="min-w-0">
        <span className="text-gray-500 text-[0.82rem]">{label}</span>
        <p className="font-medium text-gray-900 text-[0.82rem] leading-snug whitespace-pre-line">{value}</p>
      </div>
    </div>
  )
}

/* ══════════════════════════════════════════════════════════════ */
/* ═══  STUDIENVERLAUF SECTION  ══════════════════════════════= */
/* ══════════════════════════════════════════════════════════════ */

function StudienverlaufSection({ semesterGruppen, accountId: _accountId, stnId: _stnId, fortschritt, canRead }: {
  semesterGruppen: SemesterGruppe[]
  accountId?: string
  stnId?: number | null
  fortschritt: StudienFortschritt | null
  canRead: boolean
}) {
  return (
    <section>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Studienverlauf & Prüfungsleistung
      </h2>

      {!canRead ? (
        <div className="border border-gray-200 rounded-xl bg-white p-8 text-center text-sm text-gray-500">
          <span className="block font-medium text-gray-700 mb-1">Keine Berechtigung</span>
          Sie haben keine Berechtigung, die Noten dieses Teilnehmers einzusehen.
          Bitte wenden Sie sich an Ihren Firma-Administrator.
        </div>
      ) : (
        <>
          {fortschritt && fortschritt.regelCredits > 0 && (
            <FortschrittsBalken fortschritt={fortschritt} />
          )}

          {semesterGruppen.length === 0 ? (
            <div className="border border-gray-200 rounded-xl bg-white p-8 text-center text-sm text-gray-400">
              Keine Noten vorhanden
            </div>
          ) : (
            <div className="border border-gray-200 rounded-xl bg-white overflow-hidden">
              {semesterGruppen.map((gruppe, idx) => (
                <AccordionItem
                  key={gruppe.name}
                  title={gruppe.name}
                  defaultOpen={idx === 0}
                  badge={
                    gruppe.totalEcts > 0 ? (
                      <span className={`text-xs font-medium ${gruppe.erreichtEcts >= gruppe.totalEcts ? 'text-emerald-500' : 'text-gray-400'}`}>
                        {gruppe.erreichtEcts} / {gruppe.totalEcts} ECTS
                      </span>
                    ) : undefined
                  }
                >
                  <div className="space-y-4">
                    {gruppe.module.map((modul) => (
                      <ModulItem key={modul.fachId} modul={modul} />
                    ))}
                  </div>
                </AccordionItem>
              ))}
            </div>
          )}

          {/* Glossar */}
          <div className="flex items-center gap-4 mt-4 text-xs text-gray-500 flex-wrap">
            <span className="flex items-center gap-1.5">
              <Info size={13} className="text-gray-400" />
              <span className="font-semibold">Glossar:</span>
            </span>
            <span className="flex items-center gap-1.5">
              <StatusDot status="angemeldet" />
              Angemeldet oder noch nicht bewertet
            </span>
            <span className="flex items-center gap-1.5">
              <StatusDot status="bestanden" />
              Bestanden
            </span>
            <span className="flex items-center gap-1.5">
              <StatusDot status="nicht_bestanden" />
              Nicht bestanden
            </span>
          </div>
        </>
      )}
    </section>
  )
}

/* ── Fortschrittsbalken (Ist vs Soll vs Regel) ───────────────── */

function FortschrittsBalken({ fortschritt }: { fortschritt: StudienFortschritt }) {
  const { istCredits, sollCredits, regelCredits } = fortschritt
  const istPct = Math.min(100, (istCredits / regelCredits) * 100)
  const sollPct = Math.min(100, (sollCredits / regelCredits) * 100)

  return (
    <div className="border border-gray-200 rounded-xl bg-white p-6 mb-6">
      <div className="flex items-baseline justify-between mb-4">
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold text-gray-900 tabular-nums">{Math.round(istCredits)}</span>
          <span className="text-base text-gray-400">/ {regelCredits} ECTS</span>
        </div>
        <div className="text-xs text-gray-400 tabular-nums">
          Soll-Stand: {Math.round(sollCredits)} ECTS
        </div>
      </div>

      <div className="relative h-3 bg-gray-100 rounded-full overflow-visible">
        {/* Ist-Balken */}
        <div
          className="absolute inset-y-0 left-0 bg-fom-500 rounded-full transition-all duration-500"
          style={{ width: `${istPct}%` }}
        />
        {/* Soll-Marker */}
        {sollPct > 0 && sollPct < 100 && (
          <div
            className="absolute -top-1 -bottom-1 w-0.5 bg-gray-700 rounded"
            style={{ left: `${sollPct}%` }}
            title={`Soll-Stand: ${Math.round(sollCredits)} ECTS`}
          />
        )}
      </div>

      <div className="flex items-center gap-4 mt-3 text-[11px] text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-fom-500" />
          Erreicht
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-0.5 h-3 bg-gray-700 rounded" />
          Soll-Stand (laut Regelstudienzeit)
        </span>
        <span className="flex items-center gap-1.5 ml-auto">
          <span className="w-2.5 h-2.5 rounded-full bg-gray-200" />
          Regelumfang {regelCredits} ECTS
        </span>
      </div>
    </div>
  )
}

/* ── Modul-Item (Fach + Teilleistungen) ──────────────────────── */

function ModulItem({ modul }: { modul: Modul }) {
  const [open, setOpen] = useState(false)

  // Beste Note im Modul
  const noten = modul.teilleistungen.map(t => t.note).filter(n => n > 0)
  const besteNote = noten.length ? Math.min(...noten) : null
  const alleBestanden = modul.teilleistungen.every(t => t.status === 'bestanden')

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="w-full text-left bg-transparent border-none p-0 cursor-pointer group"
      >
        {/* Modulname + ECTS */}
        <div className="flex items-baseline justify-between gap-4 mb-1.5">
          <span className="text-fom-600 font-medium text-[0.9rem] group-hover:underline">
            {modul.fach}
          </span>
          <span className="text-sm text-gray-500 whitespace-nowrap flex-shrink-0">
            {modul.ects > 0 ? `${modul.ects} ECTS` : '0 ECTS'}
          </span>
        </div>

        {/* Teilleistungen */}
        <div className="flex flex-col gap-0.5 text-[0.82rem]">
          {modul.teilleistungen.map((tl) => (
            <div key={tl.id} className="flex items-center gap-1 flex-wrap">
              <StatusDot status={tl.status} />
              <span className="text-gray-700">{tl.name}</span>
              <span className="text-gray-400 ml-1">Note: {fmtNote(tl.note)}</span>
              {tl.klausurDatum && (
                <span className="text-gray-400 ml-1">
                  · Termin: {fmtKurzDatum(tl.klausurDatum)}
                  {tl.klausurUhrzeit && tl.klausurUhrzeit !== '00:00' ? `, ${tl.klausurUhrzeit} Uhr` : ''}
                </span>
              )}
              {tl.anmeldeDatum && (
                <span className="text-gray-400 ml-1">
                  · Angemeldet: {fmtKurzDatum(tl.anmeldeDatum)}
                </span>
              )}
            </div>
          ))}
        </div>
      </button>

      {/* ── Modul-Overlay ── */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setOpen(false)}>
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-fom-500 px-6 py-5 text-white">
              <h3 className="text-lg font-bold">{modul.fach}</h3>
              <div className="flex items-center gap-4 mt-2 text-sm text-white/80">
                <span>{modul.ects} ECTS</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${alleBestanden ? 'bg-emerald-500/20 text-emerald-100' : 'bg-white/15 text-white/90'}`}>
                  {alleBestanden ? 'Bestanden' : 'Offen'}
                </span>
                {besteNote !== null && (
                  <span>Modulnote: {fmtNote(besteNote)}</span>
                )}
              </div>
            </div>

            {/* Teilleistungen-Liste */}
            <div className="px-6 py-4 space-y-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Teilleistungen</h4>
              {modul.teilleistungen.map((tl) => (
                <div key={tl.id} className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
                  <div className="flex items-center gap-2.5">
                    <StatusDot status={tl.status} />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{tl.name}</div>
                      <div className="text-xs text-gray-500 capitalize">
                        {tl.status === 'bestanden' ? 'Bestanden' : tl.status === 'nicht_bestanden' ? 'Nicht bestanden' : 'Angemeldet'}
                      </div>
                      {(tl.klausurDatum || tl.anmeldeDatum) && (
                        <div className="text-[11px] text-gray-400 mt-0.5">
                          {tl.klausurDatum && (
                            <>
                              Termin: {fmtKurzDatum(tl.klausurDatum)}
                              {tl.klausurUhrzeit && tl.klausurUhrzeit !== '00:00' ? `, ${tl.klausurUhrzeit} Uhr` : ''}
                              {tl.klausurort ? ` · ${tl.klausurort}` : ''}
                            </>
                          )}
                          {tl.klausurDatum && tl.anmeldeDatum && <span className="mx-1">·</span>}
                          {tl.anmeldeDatum && <>Angemeldet: {fmtKurzDatum(tl.anmeldeDatum)}</>}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    {tl.note > 0 ? (
                      <span className={`text-lg font-bold ${tl.note <= 4.0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {fmtNote(tl.note)}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">–</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setOpen(false)}
                className="px-5 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors border-none cursor-pointer"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

/* ══════════════════════════════════════════════════════════════ */
/* ═══  SEMESTERPLÄNE SECTION  ═══════════════════════════════= */
/* ══════════════════════════════════════════════════════════════ */

function SemesterplaeneSection({ accountId, stnId }: {
  accountId?: string
  stnId?: number | null
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['semesterplaene', accountId, stnId],
    queryFn: () => api.semesterplaene(accountId!, stnId!),
    enabled: !!accountId && !!stnId,
    staleTime: 5 * 60 * 1000,
  })

  const plaene = data?.semesterplaene ?? []

  // dateiName-Format: "<lange Beschreibung>@<KÜRZEL SEMESTER ORT>"
  function splitName(name: string): { title: string; tag: string } {
    const idx = name.lastIndexOf('@')
    if (idx === -1) return { title: name, tag: '' }
    return { title: name.slice(0, idx).trim(), tag: name.slice(idx + 1).trim() }
  }

  function openPdf(plan: { dateiName: string; url: string }) {
    const m = plan.url.match(/\/getPdf\/(\d+)/i)
    const id = m ? Number(m[1]) : undefined
    if (!accountId || !stnId || !id) {
      window.alert('PDF-ID nicht ermittelbar — bitte IT-Support kontaktieren.')
      return
    }
    window.open(api.semesterplanPdfUrl(accountId, stnId, id), '_blank', 'noopener')
  }

  return (
    <section className="mt-10">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Semesterpläne
      </h2>
      <div className="border border-gray-200 rounded-xl bg-white overflow-hidden">
        {isLoading ? (
          <div className="px-4 py-6 text-center text-sm text-gray-400 italic">Semesterpläne werden geladen…</div>
        ) : plaene.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-gray-400">Keine Semesterpläne verfügbar</div>
        ) : (
          plaene.map((plan, i) => {
            const { title, tag } = splitName(plan.dateiName)
            return (
              <button
                key={`${plan.url}-${i}`}
                onClick={() => openPdf(plan)}
                className="w-full border-b border-gray-100 last:border-b-0 px-4 py-3.5 flex items-start justify-between gap-3 text-left hover:bg-gray-50/50 transition-colors cursor-pointer"
              >
                <span className="font-medium text-gray-800 text-[0.9rem] flex-1">{title}</span>
                {tag && <span className="text-xs text-gray-400 whitespace-nowrap pt-0.5">{tag}</span>}
              </button>
            )
          })
        )}
      </div>
    </section>
  )
}

/* ══════════════════════════════════════════════════════════════ */
/* ═══  PRÜFUNGSORDNUNGEN SECTION  ═══════════════════════════ */
/* ══════════════════════════════════════════════════════════════ */

function PruefungsordnungenSection({
  accountId,
  stnId,
  noten,
}: {
  accountId?: string
  stnId?: number
  noten: Note[]
}) {
  // PO-Strings aus den Noten als Hint (für leere Liste / Header) — sonst nicht weiter benutzt,
  // weil die echte Doku-Liste aus dem DocumentService kommt.
  const poFromNoten = new Set<string>()
  for (const n of noten) {
    if (n.PO) poFromNoten.add(n.PO)
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['pruefungsordnungen', accountId, stnId],
    queryFn: () => api.pruefungsordnungen(accountId!, stnId!),
    enabled: !!accountId && !!stnId,
    staleTime: 60 * 60 * 1000,  // 1h — POs ändern sich selten
  })

  const items = data?.items ?? []

  function openPdf(fileItem: { dateiName: string; url: string; id?: number }) {
    // Backend-Proxy-URL für das PDF. fileItem.id ist bevorzugt; sonst
    // versucht der Code, die ID aus dem url-Feld zu extrahieren (Pattern ".../getPdf/<id>").
    let id: number | undefined = fileItem.id
    if (id === undefined) {
      const m = fileItem.url.match(/\/getPdf\/(\d+)/i)
      if (m) id = Number(m[1])
    }
    if (!accountId || !stnId || !id) {
      window.alert('PDF-ID nicht ermittelbar — bitte IT-Support kontaktieren.')
      return
    }
    const url = api.pruefungsordnungPdfUrl(accountId, stnId, id)
    window.open(url, '_blank', 'noopener')
  }

  return (
    <section className="mt-10 mb-10">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Prüfungsordnungen
        {poFromNoten.size > 0 && (
          <span className="ml-3 text-sm font-normal text-gray-500">
            ({Array.from(poFromNoten).join(', ')})
          </span>
        )}
      </h2>
      <div className="border border-gray-200 rounded-xl bg-white overflow-hidden">
        {isLoading && (
          <div className="py-6 px-4 text-sm text-gray-500">Lade Prüfungsordnungs-Dokumente…</div>
        )}
        {error && (
          <div className="py-6 px-4 text-sm text-rose-600">
            Dokumente konnten nicht geladen werden ({(error as Error).message}).
          </div>
        )}
        {!isLoading && !error && items.length === 0 && (
          <div className="py-6 px-4 text-sm text-gray-500">
            Für diese Studi-Datensätze sind aktuell keine Prüfungsordnungs-Dokumente verfügbar.
          </div>
        )}
        {items.map((item) => (
          <button
            key={`${item.dateiName}-${item.url}`}
            onClick={() => openPdf(item)}
            className="w-full flex items-center justify-between py-3.5 px-4 text-left hover:bg-gray-50/50 transition-colors border-b border-gray-100 last:border-b-0 cursor-pointer"
          >
            <span className="font-medium text-gray-800 text-[0.9rem]">{item.dateiName}</span>
            <ChevronRight size={18} className="text-gray-400" />
          </button>
        ))}
      </div>
    </section>
  )
}
